import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

interface DemoAccount {
  key: string;
  icon: string;
  email: string;
  title: string;
  route: string;
}

const DEMO_ACCOUNTS: DemoAccount[] = [
  { key: 'coordinator', icon: '\uD83D\uDC69\u200D\u2695\uFE0F', email: 'coordinator@d3ri.ai', title: 'Site Coordinator', route: '/home/coordinator' },
  { key: 'monitor', icon: '\uD83D\uDD0D', email: 'monitor@d3ri.ai', title: 'Clinical Monitor', route: '/home/monitor' },
  { key: 'budget', icon: '\uD83D\uDCB0', email: 'budget@d3ri.ai', title: 'Budget Coordinator', route: '/home/budget' },
];

function mapEmailToRole(email: string): { role: string; route: string } {
  const lower = email.toLowerCase();

  if (lower === 'girish@d3ri.ai' || lower === 'd3ri.girish@gmail.com') {
    return { role: 'admin', route: '/role-select' };
  }
  if (lower.endsWith('@berkeley.edu') || lower.endsWith('@ucsf.edu') || lower.endsWith('@johnshopkins.edu')) {
    return { role: 'monitor', route: '/home/monitor' };
  }
  if (lower.endsWith('@ucla.edu')) {
    return { role: 'coordinator', route: '/home/coordinator' };
  }
  return { role: 'admin', route: '/role-select' };
}

export default function Login() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const email = user.email || '';
      const { role, route } = mapEmailToRole(email);

      localStorage.setItem('d3ri_logged_in', 'true');
      localStorage.setItem('d3ri_user_email', email);
      localStorage.setItem('d3ri_user_name', user.displayName || '');
      localStorage.setItem('d3ri_user_photo', user.photoURL || '');
      localStorage.setItem('d3ri_selected_role', role);

      navigate(route);
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled.');
      } else {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = (account: DemoAccount) => {
    localStorage.setItem('d3ri_logged_in', 'true');
    localStorage.setItem('d3ri_user_email', account.email);
    localStorage.setItem('d3ri_user_name', account.title);
    localStorage.setItem('d3ri_user_photo', '');
    localStorage.setItem('d3ri_selected_role', account.key);
    navigate(account.route);
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>D3RI</div>
        <div style={styles.tagline}>Clinical Trial Decision Intelligence</div>

        <button
          style={{
            ...styles.googleBtn,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onClick={handleGoogleSignIn}
          disabled={loading}
          onMouseEnter={(e) => { if (!loading) (e.currentTarget.style.backgroundColor = '#f8f8f8'); }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
        >
          <svg width="20" height="20" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          <span>{loading ? 'Signing in...' : 'Sign in with Google'}</span>
        </button>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.divider}>
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>or use demo account</span>
          <span style={styles.dividerLine} />
        </div>

        <div style={styles.demoGrid}>
          {DEMO_ACCOUNTS.map((account) => (
            <button
              key={account.key}
              style={styles.demoCard}
              onClick={() => handleDemoLogin(account)}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e293b'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1a202c'; }}
            >
              <span style={styles.demoIcon}>{account.icon}</span>
              <div>
                <div style={styles.demoTitle}>{account.title}</div>
                <div style={styles.demoEmail}>{account.email}</div>
              </div>
            </button>
          ))}
        </div>
        <button
          style={styles.howItWorksBtn}
          onClick={() => navigate('/how-it-works')}
        >
          How It Works
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0d1117',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px 36px',
    backgroundColor: '#161b22',
    borderRadius: 16,
    border: '1px solid #1a365d',
  },
  logo: {
    fontSize: 48,
    fontWeight: 800,
    color: '#63b3ed',
    letterSpacing: 4,
    marginBottom: 4,
  },
  tagline: {
    fontSize: 13,
    color: '#718096',
    letterSpacing: 1,
    marginBottom: 40,
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
    padding: '14px 20px',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: 16,
    fontWeight: 600,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    fontFamily: 'inherit',
  },
  error: {
    marginTop: 12,
    fontSize: 13,
    color: '#f56565',
    textAlign: 'center',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    margin: '32px 0',
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#2d3748',
  },
  dividerText: {
    fontSize: 12,
    color: '#4a5568',
    whiteSpace: 'nowrap',
  },
  demoGrid: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  demoCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    padding: '14px 16px',
    backgroundColor: '#1a202c',
    border: '1px solid #2d3748',
    borderRadius: 10,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s',
    fontFamily: 'inherit',
  },
  demoIcon: {
    fontSize: 28,
    flexShrink: 0,
  },
  demoTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#e2e8f0',
  },
  demoEmail: {
    fontSize: 12,
    color: '#718096',
    marginTop: 2,
  },
  howItWorksBtn: {
    width: '100%',
    padding: '12px 20px',
    marginTop: 24,
    backgroundColor: 'transparent',
    border: '1px solid #2d3748',
    borderRadius: 8,
    color: '#63b3ed',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    fontFamily: 'inherit',
  },
};
